import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild, Inject } from '@angular/core';
import { Observable, Subscription, of, combineLatest, concat } from 'rxjs';
import { map, tap, takeWhile, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { Store, Select } from '@ngxs/store';
import { ToastrService, ToastContainerDirective } from 'ngx-toastr';
import { MenuState } from '@nimbus-library/features/menu';
import { TranslationComponentObserverService } from '@nimbus-library/features/localization/services/translation-component-observer.service';
import { TranslationsReadyService } from '@nimbus-library/features/localization/services/translations-ready.service';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { ITeamsContext } from '@nimbus-library/features/microsoft-teams/models/i-teams.context';
import { AppUpdateService } from '@nimbus-global/features/shared/services/app-update.service';
import { CompanionSplitScreenService } from '@nimbus-library/features/companion-chat/services/companion-split-screen.service';
import { GlobalHeaderBarVisibilityService } from '@nimbus-portal/common/services/global-header-bar-visibility.service';
import { APP_MODE, AppMode } from './features/app-mode/app-mode';
import { IInitializationService } from './features/initialization/i-initialization.service';
import { TeamState } from './common/store/teams/teams.state';

@Component({
  standalone: false,
  selector: 'nimbus-app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TranslationComponentObserverService],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild(ToastContainerDirective, { static: true }) public toastContainer: ToastContainerDirective;

  @Select(TeamState.loading)
  public loading$: Observable<boolean>;
  @Select(TeamState.loaded)
  public loadedState$: Observable<boolean>;

  private readonly subscription$: Subscription;

  public title = 'Nimbus';
  public showLoadingScreen$: Observable<boolean>;
  public translationsReady$: Observable<boolean>;
  public context$: Observable<ITeamsContext>;
  public loaded$: Observable<boolean>;
  public showMenu$: Observable<boolean>;
  // Hide the header bar on the same routes the menu hides on: showMenu$ only reflects menu load, the route gate lives in the menu's filter$.
  public showAppChrome$: Observable<boolean>;
  // The global header bar is additionally gated behind the GlobalHeaderBar feature flag AND the tenant-admin opt-in setting.
  // When either is off, the old functionality remains (header bar hidden).
  public showHeaderBar$: Observable<boolean>;
  public isStandAlone: boolean;
  public isAuthenticated$: Observable<boolean>;
  public companionChatActive$: Observable<boolean>;

  public constructor(
    @Inject(APP_MODE) private readonly appMode: AppMode,
    private readonly store: Store,
    private readonly changeDetector: ChangeDetectorRef,
    public readonly applicationElement: ElementRef,
    private readonly authenticationService: IAuthenticationService,
    private readonly initializationService: IInitializationService,
    private readonly teamsService: IMicrosoftTeamsContextAccessor,
    private readonly toastrService: ToastrService,
    private readonly companionSplitScreenService: CompanionSplitScreenService,
    private readonly globalHeaderBarVisibilityService: GlobalHeaderBarVisibilityService,
    private readonly translationsReadyService: TranslationsReadyService,
    appUpdateService: AppUpdateService,
  ) {
    this.subscription$ = new Subscription();
    appUpdateService.initialize();
  }

  public ngOnInit(): void {
    this.toastrService.overlayContainer = this.toastContainer;
    this.context$ = this.initializationService.context$;
    this.translationsReady$ = this.translationsReadyService.translationsReady$;
    this.showLoadingScreen$ = this.translationsReady$.pipe(map((ready) => !ready));
    this.showMenu$ = this.store.select(MenuState.show);
    const menuRouteFilter$ = this.store.select(MenuState.menu).pipe(
      distinctUntilChanged(),
      switchMap((menu) => (menu ? menu.filter$ : of(false))),
    );
    this.showAppChrome$ = combineLatest([this.showMenu$, menuRouteFilter$]).pipe(
      map(([showMenu, routeAllowsMenu]) => showMenu && routeAllowsMenu),
    );

    this.showHeaderBar$ = combineLatest([this.showAppChrome$, this.globalHeaderBarVisibilityService.isActive$]).pipe(
      map(([showAppChrome, isHeaderBarActive]) => showAppChrome && isHeaderBarActive),
      distinctUntilChanged(),
    );
    this.companionChatActive$ = this.appMode !== AppMode.Wallboard ? this.companionSplitScreenService.active$ : of(false);

    this.subscription$.add(
      combineLatest([this.initializationService.routeData$, concat(of(false), this.loadedState$)])
        .pipe(
          map(([options, loaded]) => ({ loadContext: options.loadContext, loaded })),
          tap((options) => {
            this.loaded$ = options.loadContext ? this.loadedState$ : of(true);
            this.changeDetector.detectChanges();
          }),
          takeWhile((options) => !options.loaded),
        )
        .subscribe(),
    );

    this.isStandAlone = this.teamsService.isStandAlone();
    this.isAuthenticated$ = this.authenticationService.isAuthenticated$;
    this.initializationService.initialize().then((subscription$) => this.subscription$.add(subscription$));
  }

  public ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }
}
